import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Right-hand drill drawer — the shell every report drilldown opens in (GL
 * detail, booking pace, source files, source documents). The page stays
 * visible behind it; ✕, Escape or a click outside closes it.
 *
 *   size   'default' 560px lists · 'table' 760px tables · 'wide' 1040px chart + table
 *   escape pass false while something stacked on top (a file viewer) owns Escape
 *   head   kicker / title / meta on the left, value (+ note) on the right, nav row below
 *   docked non-modal: no backdrop, the report stays usable (and visible beside
 *          the drawer on wide screens) so another cell can retarget it
 */
export function ReportDrawer({
  open = true,
  onClose,
  size = 'default',
  label,
  kicker,
  title,
  meta,
  value,
  valueNote,
  valueTone,
  nav,
  footer,
  children,
  escape = true,
  docked = false,
  bodyClassName = '',
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open || !escape) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, escape]);

  // A docked drawer marks the body so other floating surfaces (Lodgeic AI)
  // layer in front of it and dock beside it.
  useEffect(() => {
    if (!open || !docked) return undefined;
    document.body.classList.add('rn-has-docked-drawer');
    return () => document.body.classList.remove('rn-has-docked-drawer');
  }, [open, docked]);

  if (!open) return null;
  const hasValue = value !== undefined && value !== null && value !== '';

  const panel = (
    <aside
      className={`rn-rv-drawer rn-drawer rn-drawer-${size}${docked ? ' rn-drawer-docked' : ''}`}
      role="dialog"
      aria-modal={docked ? 'false' : 'true'}
      aria-label={label || (typeof title === 'string' ? title : kicker)}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="rn-drawer-head">
        {kicker ? <div className="rn-drawer-kicker">{kicker}</div> : <div />}
        <button type="button" className="rn-icon-btn rn-drawer-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        {title ? <div className="rn-drawer-title">{title}</div> : null}
        {hasValue ? (
          <div className="rn-drawer-value">
            <b>{value}</b>
            {valueNote ? <span className={`rn-drawer-value-note${valueTone ? ` rn-drawer-value-${valueTone}` : ''}`}>{valueNote}</span> : null}
          </div>
        ) : null}
        {meta ? <div className="rn-drawer-meta">{meta}</div> : null}
        {nav ? <div className="rn-drawer-nav">{nav}</div> : null}
      </header>
      <div className={`rn-drawer-body ${bodyClassName}`.trim()}>{children}</div>
      {footer ? <footer className="rn-drawer-foot">{footer}</footer> : null}
    </aside>
  );
  return createPortal(
    docked ? panel : <div className="rn-modal-backdrop rn-rv-drawer-backdrop" onClick={onClose}>{panel}</div>,
    document.body
  );
}

/**
 * ‹ label › stepper for drawer navigation (day / month). `pick` adds a native
 * date or month picker behind the label: { type: 'date' | 'month', value, max, onChange }.
 */
export function DrawerStepper({ label, onPrev, onNext, prevDisabled, nextDisabled, pick, prevLabel = 'Previous', nextLabel = 'Next' }) {
  const input = useRef(null);
  return (
    <span className="rn-drawer-stepper">
      <button type="button" className="rn-drawer-step" onClick={onPrev} disabled={prevDisabled} aria-label={prevLabel}>
        <ChevronLeft size={14} />
      </button>
      {pick ? (
        <>
          <button type="button" className="rn-drawer-pill" onClick={() => input.current?.showPicker?.()} aria-label={`Pick ${pick.type === 'month' ? 'a month' : 'a day'}`}>
            {label} <span aria-hidden="true">▾</span>
          </button>
          <input
            ref={input}
            type={pick.type}
            className="rn-drawer-pick-input"
            value={pick.value || ''}
            max={pick.max}
            onChange={(e) => e.target.value && pick.onChange(e.target.value)}
            aria-label={pick.type === 'month' ? 'Month' : 'Day'}
            tabIndex={-1}
          />
        </>
      ) : (
        <span className="rn-drawer-pill rn-drawer-pill-static">{label}</span>
      )}
      <button type="button" className="rn-drawer-step" onClick={onNext} disabled={nextDisabled} aria-label={nextLabel}>
        <ChevronRight size={14} />
      </button>
    </span>
  );
}

export default ReportDrawer;
